// Searchable multi-select for ISO 3166-1 alpha-2 country/region codes.
// Presentational — caller fetches `options` once (e.g. via useRegions()) and passes it down,
// so it can be shared across many rendered instances without refetching per instance.

import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface CountryOption {
  label: string;
  value: string; // ISO alpha-2
}

interface Props {
  value: string[];
  onChange: (values: string[]) => void;
  options: CountryOption[];
  placeholder?: string;
}

export function CountryMultiSelect({ value, onChange, options, placeholder }: Props) {
  const { t } = useTranslation('admin');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = options
    .filter(
      (o) =>
        !value.includes(o.value) &&
        (o.label.toLowerCase().includes(search.toLowerCase()) ||
          o.value.toLowerCase().includes(search.toLowerCase()))
    )
    .slice(0, 50);

  const add = (code: string) => {
    onChange([...value, code]);
    setSearch('');
    setOpen(true);
    // setTimeout defers focus() past the current event cycle so it always wins
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const remove = (code: string) => {
    onChange(value.filter((v) => v !== code));
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Close only when focus truly leaves the component — relatedTarget tells us where focus went
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (containerRef.current?.contains(e.relatedTarget as Node)) return;
    setOpen(false);
    setSearch('');
  };

  const labelFor = (code: string) => options.find((o) => o.value === code)?.label ?? code;

  return (
    <div ref={containerRef} className="relative">
      {/* Selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {value.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm font-medium bg-primary/20 text-primary"
            >
              {labelFor(code)} ({code})
              <button
                type="button"
                onClick={() => remove(code)}
                className="hover:text-error leading-none"
                aria-label={t('countryMultiSelect.remove', { country: labelFor(code) })}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <input
        ref={inputRef}
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        placeholder={placeholder ?? t('countryMultiSelect.search')}
        className="input input-bordered input-sm w-full"
      />

      {/* Dropdown */}
      {open && filtered.length > 0 && (
        <ul className="absolute z-30 left-0 right-0 top-full mt-1 bg-base-100 border border-base-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                onClick={() => add(o.value)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-base-200 flex items-center gap-2"
              >
                <span className="text-base-content/40 font-mono text-sm w-12 shrink-0">
                  {o.value}
                </span>
                <span>{o.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
